<script setup lang="ts">
defineProps<{
  company: string
  role: string
  period: string
  current: boolean
  description: string
  techStack: string[]
  highlights?: string[]
  expanded: boolean
}>()

defineEmits<{ toggle: [] }>()
</script>

<template>
  <div class="relative pl-7 pb-6 last:pb-0 md:pl-8 md:pb-8">
    <!-- Timeline dot -->
    <div class="absolute left-0 top-1.5 size-2.5 rounded-full border-2 border-primary bg-background" />

    <!-- Mobile: clickable header button; Desktop: plain header -->
    <button
      type="button"
      class="w-full text-left md:pointer-events-none md:cursor-default"
      :aria-expanded="expanded"
      @click="$emit('toggle')"
    >
      <div class="flex flex-wrap items-center gap-2 mb-0.5">
        <h3 class="font-semibold text-highlighted text-base">
          {{ company }}
        </h3>
        <UBadge
          v-if="current"
          color="success"
          variant="subtle"
          size="sm"
          class="text-zinc-700 dark:text-zinc-200"
        >
          Current
        </UBadge>
        <UIcon
          :name="expanded ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'"
          class="ml-auto text-muted size-4 md:hidden"
        />
      </div>

      <p class="text-sm text-muted mb-0.5">
        {{ role }}
      </p>
      <p class="text-sm text-muted opacity-60">
        {{ period }}
      </p>
    </button>

    <!-- Expandable content: hidden on mobile when collapsed, always visible on desktop -->
    <Transition
      enter-active-class="transition-all duration-300 ease-out overflow-hidden"
      enter-from-class="opacity-0 max-h-0"
      enter-to-class="opacity-100 max-h-[1000px]"
      leave-active-class="transition-all duration-300 ease-in overflow-hidden"
      leave-from-class="opacity-100 max-h-[1000px]"
      leave-to-class="opacity-0 max-h-0"
    >
      <div
        v-show="expanded"
        class="md:!block md:!opacity-100 md:!max-h-none mt-3"
      >
        <p class="text-sm text-muted leading-relaxed mb-3">
          {{ description }}
        </p>

        <ul
          v-if="highlights?.length"
          class="space-y-1 mb-3"
        >
          <li
            v-for="highlight in highlights"
            :key="highlight"
            class="flex items-start gap-2 text-sm text-muted"
          >
            <UIcon
              name="i-lucide-check"
              class="text-primary mt-0.5 shrink-0 size-3.5"
            />
            {{ highlight }}
          </li>
        </ul>

        <div class="flex flex-wrap gap-1.5">
          <UBadge
            v-for="tech in techStack"
            :key="tech"
            variant="subtle"
            size="md"
            class="text-zinc-700 dark:text-zinc-200"
          >
            {{ tech }}
          </UBadge>
        </div>
      </div>
    </Transition>
  </div>
</template>
